import React, { useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "../../lib/api";
import { FileText, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function FileUpload({ value, onChange, accept = "image/*", label = "Image", testid = "file-upload", returnFilename = false, filename = "" }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            // Use the name from the selected File as the source of truth. The
            // upload id is only an internal reference and must not be shown as
            // (or substituted for) the user's filename.
            onChange(returnFilename ? { id: data.id, filename: file.name } : data.id);
            toast.success("Uploaded");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
        }
    };

    const isImg = accept.includes("image");
    const namedValue = value && filename && !/^https?:/i.test(String(value))
        ? `/api/files/${String(value).replace(/^.*\/files\//, "").split("/")[0]}/${encodeURIComponent(filename)}`
        : value;
    const preview = value ? fileUrl(namedValue) : null;

    return (
        <div data-testid={testid} className="min-w-0 rounded-xl border border-black/10 bg-[#F8FAFC] p-4 sm:p-5">
            <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#475569]">{label}</label>
            <div className="mt-3 flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
                {preview && isImg && (
                    <div className="relative w-32 h-20 bg-[#002951] overflow-hidden">
                        <img src={preview} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 bg-black/60 text-white p-1">
                            <X size={12} />
                        </button>
                    </div>
                )}
                {preview && !isImg && (
                    <a href={preview} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-3 rounded-lg bg-white p-3 text-[#0060A7] shadow-sm ring-1 ring-black/5 hover:ring-[#0060A7]/30">
                        <FileText size={22} className="shrink-0" />
                        <span className="min-w-0 truncate text-sm font-medium" title={filename || "PDF"}>{filename || "PDF"}</span>
                    </a>
                )}
                <div className="min-w-0 sm:shrink-0">
                    <button
                        type="button"
                        onClick={() => ref.current?.click()}
                        disabled={busy}
                        data-testid={`${testid}-btn`}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#002951] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0060A7] disabled:opacity-60 sm:w-auto"
                    >
                        <Upload size={14} />
                        {busy ? "..." : "Upload"}
                    </button>
                    <input
                        ref={ref} type="file" accept={accept} className="hidden"
                        onChange={(e) => upload(e.target.files?.[0])}
                    />
                    <input
                        type="text"
                        value={returnFilename && filename ? filename : (value || "")}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="or paste URL"
                        readOnly={returnFilename && Boolean(filename)}
                        className="mt-2 block w-full min-w-0 truncate rounded-md border border-black/10 bg-white px-3 py-2 text-xs text-[#475569] outline-none focus:border-[#0060A7] sm:w-52"
                    />
                </div>
            </div>
        </div>
    );
}
